import { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosInstance';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Hook for the Image alt-text inventory page.
 *
 * Read-only status view (no per-row policy editing): translation happens via the
 * bulk image_alt CLI pipeline / nightly sync. Filters are SERVER-SIDE — every
 * change refetches with query params. Mirrors useContentResources.js minus the
 * policy PATCH + field_key plumbing (image alt is a single 'alt' field).
 */
export default function useImageAlts() {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters (server-side)
  const [resourceType, setResourceType] = useState(''); // '' | MEDIA_IMAGE | COLLECTION_IMAGE | ARTICLE_IMAGE
  const [policy, setPolicy] = useState('all');           // 'all' | 'translate' | 'skip' | 'auto-skipped'
  const [status, setStatus] = useState('all');           // 'all' | 'missing'
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // Debounce search input (300ms — matches the other inventory pages)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (resourceType) params.resource_type = resourceType;
      if (policy && policy !== 'all') params.policy = policy;
      if (status && status !== 'all') params.status = status;
      if (debouncedQ) params.q = debouncedQ;

      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/image-alts/', { params }),
        axios.get('/api/transly/image-alts/sync-status/'),
      ]);
      setRows(listRes.data.rows || []);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load image alt-text');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, resourceType, policy, status, debouncedQ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to page 1 when filters change (avoid stranded pages).
  useEffect(() => { setPage(1); }, [resourceType, policy, status, debouncedQ]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/image-alts/sync/');
  }, []);

  return {
    rows, total, loading, error,
    resourceType, setResourceType,
    policy, setPolicy,
    status, setStatus,
    q, setQ,
    page, setPage, pageSize, setPageSize,
    syncModalOpen, setSyncModalOpen, lastSynced, triggerSync,
    fetchData,
  };
}
